// engine-exception: portal shell — branding + navigation come from the tenant's portal settings and menu buttons (/portal/v1/me), not from ModuleUI nav; mobile-first bottom tab bar
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../engine/design/icons';
import { Avatar, EmptyState, Loading } from '../engine/design/primitives/Misc';
import { Drawer, DropdownMenu } from '../engine/design/primitives/Overlay';
import { buttonHref, isExternal, PortalProvider, safeIcon, usePortalMe } from './PortalContext';
import type { Me, MenuButton } from './types';

const MAX_TABS = 4;

export function PortalShell({ onSignOut }: { onSignOut: () => void | Promise<void> }) {
  const me = usePortalMe();
  const location = useLocation();
  if (me.isLoading) return <div className="pt-auth"><Loading label="Opening your portal…" /></div>;
  if (me.error) {
    if (me.error.status === 401) return <Navigate to="/portal/login" replace state={{ from: location.pathname + location.search }} />;
    const staff = me.error.status === 403 && /staff/i.test(me.error.detail);
    return (
      <div className="pt-auth">
        <div className="pt-auth-card">
          <EmptyState
            title={staff ? 'This sign-in belongs to the staff app' : me.error.status === 403 ? 'Portal access is not active' : 'Could not open the portal'}
            hint={staff ? 'Customer portal accounts are separate from staff accounts.' : me.error.detail}
            icon={me.error.status === 403 ? 'lock' : 'alert-circle'}
            action={<div className="hf-row">{staff && <Link className="hf-btn primary" to="/">Open the staff app</Link>}<button className="hf-btn ghost" onClick={() => void onSignOut()}>Sign out</button></div>}
          />
        </div>
      </div>
    );
  }
  return (
    <PortalProvider me={me.data!}>
      <Frame me={me.data!} onSignOut={onSignOut}>
        <Outlet />
      </Frame>
    </PortalProvider>
  );
}

function Frame({ me, onSignOut, children }: { me: Me; onSignOut: () => void | Promise<void>; children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [more, setMore] = useState(false);
  useEffect(() => setMore(false), [location.pathname]);
  const nav = useMemo(() => me.menu_buttons.filter((b) => b.show_in_nav).sort((a, b) => a.sort - b.sort), [me.menu_buttons]);
  const tabs = nav.slice(0, MAX_TABS);
  const overflow = nav.slice(MAX_TABS);
  const style = { '--pt-accent': me.branding.accent_colour ?? undefined } as CSSProperties;
  const unread = me.counts.unread_notifications;
  const brand = me.branding.name ?? me.tenant.name;
  const initials = (brand ?? 'P').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  const item = (b: MenuButton, cls?: string) =>
    isExternal(b) ? (
      <a key={b.id} href={b.route} target="_blank" rel="noopener noreferrer" className={cls}><Icon name={safeIcon(b.icon, 'link')} size={16} />{b.label}</a>
    ) : (
      <NavLink key={b.id} to={buttonHref(b)} end={b.function === 'home'} className={({ isActive }) => [cls, isActive && 'active'].filter(Boolean).join(' ')}>
        <Icon name={safeIcon(b.icon)} size={16} />
        {b.label}
      </NavLink>
    );

  return (
    <div className="pt-root" style={style} data-testid="portal-shell">
      <a className="hf-skip" href="#pt-main">Skip to content</a>
      <header className="pt-top">
        <div className="pt-top-inner">
          <Link to="/portal" className="pt-brand" aria-label={`${brand} home`}>
            {me.branding.logo_url ? <img src={me.branding.logo_url} alt="" /> : <span className="pt-brand-mark" aria-hidden>{initials}</span>}
            <span>{brand}</span>
          </Link>
          <nav className="pt-nav" aria-label="Portal">{nav.map((b) => item(b))}</nav>
          <div className="pt-top-tools">
            <Link to="/portal/notifications" className="hf-iconbtn pt-bell" aria-label={unread ? `${unread} unread notifications` : 'Notifications'}>
              <Icon name="bell" size={18} />
              {unread > 0 && <span className="pt-count">{unread > 99 ? '99+' : unread}</span>}
            </Link>
            <DropdownMenu
              trigger={<button className="hf-iconbtn" aria-label="Account menu" type="button"><Avatar name={me.contact.name} size="sm" /></button>}
              items={[
                { key: 'who', label: `${me.contact.name}${me.client ? ` · ${me.client.name}` : ''}`, heading: true },
                { key: 'profile', label: 'Profile & preferences', icon: 'user', onSelect: () => navigate('/portal/profile') },
                { key: 'sep', label: '', separator: true },
                { key: 'out', label: 'Sign out', icon: 'log-out', onSelect: () => void onSignOut() },
              ]}
            />
          </div>
        </div>
      </header>
      <main id="pt-main" className="pt-main">{children}</main>
      <nav className="pt-tabs" aria-label="Portal sections">
        {tabs.map((b) => item(b))}
        {overflow.length > 0 && (
          <a href="#more" onClick={(e) => { e.preventDefault(); setMore(true); }} aria-haspopup="dialog"><Icon name="menu" size={16} />More</a>
        )}
      </nav>
      <Drawer open={more} onClose={() => setMore(false)} title="More">
        <div className="pt-sheet">
          {overflow.map((b) => item(b))}
          <NavLink to="/portal/notifications" className={({ isActive }) => (isActive ? 'active' : '')}><Icon name="bell" size={16} />Notifications{unread > 0 ? ` (${unread})` : ''}</NavLink>
          <a href="#signout" onClick={(e) => { e.preventDefault(); void onSignOut(); }}><Icon name="log-out" size={16} />Sign out</a>
        </div>
      </Drawer>
    </div>
  );
}
